/**
 * Utility for isomorphic chat URL token encryption/decryption and contact masking.
 * Works synchronously in both browser (client-side) and Node.js (server-side).
 */

const CHAT_CIPHER_KEY = 'whatscrm_chat_enc_key_v1';

function fnv1a(str: string): number {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function getKeystream(key: string, salt: string, length: number): number[] {
  let state = fnv1a(`${key}:${salt}`);
  const stream: number[] = [];
  for (let i = 0; i < length; i++) {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    state = state >>> 0;
    stream.push(state & 0xff);
  }
  return stream;
}

function b64urlEncode(str: string): string {
  let b64: string;
  if (typeof Buffer !== 'undefined') {
    b64 = Buffer.from(str, 'binary').toString('base64');
  } else if (typeof btoa !== 'undefined') {
    b64 = btoa(str);
  } else {
    return '';
  }
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(str: string): string {
  let b64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(b64, 'base64').toString('binary');
  } else if (typeof atob !== 'undefined') {
    return atob(b64);
  }
  return '';
}

/**
 * Encrypts a jid and optional instanceId into a URL-safe token.
 * Example output: "enc_a9f1b2_X8kM2q..."
 */
export function encryptChatToken(jid: string, instanceId?: number | string | null): string {
  if (!jid) return '';
  const cleanJid = jid.trim();
  const instIdNum = instanceId !== undefined && instanceId !== null && instanceId !== ''
    ? parseInt(String(instanceId), 10)
    : undefined;

  const payload = JSON.stringify({
    j: cleanJid,
    i: !isNaN(instIdNum as number) ? instIdNum : undefined,
  });

  // UTF-8 string to binary byte array
  const rawBytes: number[] = [];
  const utf8Str = unescape(encodeURIComponent(payload));
  for (let i = 0; i < utf8Str.length; i++) {
    rawBytes.push(utf8Str.charCodeAt(i));
  }

  // 6-char random alphanumeric salt
  const salt = Math.random().toString(36).substring(2, 8);
  const keystream = getKeystream(CHAT_CIPHER_KEY, salt, rawBytes.length);
  const cipherBytes = rawBytes.map((b, idx) => b ^ keystream[idx]);
  const cipherBinary = String.fromCharCode(...cipherBytes);
  const cipherB64 = b64urlEncode(cipherBinary);

  return `enc.${salt}.${cipherB64}`;
}

/**
 * Checks whether a given string is an encrypted chat token.
 */
export function isEncryptedChatToken(token?: string | null): boolean {
  if (!token || typeof token !== 'string') return false;
  return token.startsWith('enc.') && token.split('.').length === 3;
}

/**
 * Decrypts an encrypted chat token back to { jid, instanceId }.
 * Returns null if token is invalid or cannot be decrypted.
 */
export function decryptChatToken(token?: string | null): { jid: string; instanceId?: number } | null {
  if (!token || !isEncryptedChatToken(token)) return null;

  const parts = token.slice(4).split('.');
  if (parts.length !== 2) return null;
  const [salt, cipherB64] = parts;

  try {
    const cipherBinary = b64urlDecode(cipherB64);
    if (!cipherBinary) return null;
    const cipherBytes = Array.from(cipherBinary).map((c) => c.charCodeAt(0));
    const keystream = getKeystream(CHAT_CIPHER_KEY, salt, cipherBytes.length);
    const rawBytes = cipherBytes.map((b, idx) => b ^ keystream[idx]);
    const rawBinary = String.fromCharCode(...rawBytes);
    const payload = decodeURIComponent(escape(rawBinary));
    const parsed = JSON.parse(payload);
    if (!parsed || typeof parsed.j !== 'string') return null;

    return {
      jid: parsed.j,
      instanceId: typeof parsed.i === 'number' ? parsed.i : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Masks the middle 5 digits of a phone number with asterisks (*****).
 * e.g. "+923099914748" -> "+923*****4748"
 * e.g. "923099914748"  -> "923*****4748"
 */
export function maskPhone(phone?: string | null): string {
  if (!phone || typeof phone !== 'string') return '';
  const trimmed = phone.trim();
  if (
    trimmed.startsWith('web_') ||
    trimmed.endsWith('@web.chat') ||
    trimmed.startsWith('fb_') ||
    trimmed.endsWith('@facebook.messenger') ||
    trimmed.startsWith('ig_') ||
    trimmed.endsWith('@instagram.messenger') ||
    trimmed.endsWith('@instagram.direct') ||
    trimmed.startsWith('@')
  ) return trimmed;

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length <= 5) {
    return hasPlus ? `+${'*'.repeat(digits.length)}` : '*'.repeat(digits.length);
  }

  const maskLen = 5;
  const rem = digits.length - maskLen;
  const startLen = Math.floor(rem / 2);
  const endLen = rem - startLen;

  const start = digits.slice(0, startLen);
  const end = digits.slice(digits.length - endLen);
  return `${hasPlus ? '+' : ''}${start}*****${end}`;
}

/**
 * Masks the middle characters of an email address.
 * e.g. "unispanwar@gmail.com" -> "un*****ar@gmail.com"
 * e.g. "test@example.com" -> "t*****t@example.com"
 */
export function maskEmail(email?: string | null): string {
  if (!email || typeof email !== 'string') return '';
  const parts = email.trim().split('@');
  if (parts.length !== 2) return '*****';
  const [user, domain] = parts;
  if (user.length <= 2) {
    return `${user[0]}*****@${domain}`;
  }
  const startLen = Math.min(2, Math.floor(user.length / 3)) || 1;
  const endLen = Math.min(2, Math.floor(user.length / 3)) || 1;
  const start = user.slice(0, startLen);
  const end = user.slice(user.length - endLen);
  return `${start}*****${end}@${domain}`;
}
